import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ExportDataResponse, fetchCreateExportTask, fetchGetExportResult } from "api/services/useExports";
import { QUERY_NAMING } from "config/copies/query";
import { useAppSelector } from "hooks/store";
import { usePagination } from "hooks/usePagination";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";

const ITEMS_PER_PAGE_OPTIONS = [10, 100, 500, 1000, 2000, 4000, 10000];
const MAX_POLLING_ATTEMPTS = 5;

export function AdminExportDataPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [valueId, setValueId] = useState<string>("");
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoadingAll, setIsLoadingAll] = useState<boolean>(false);
  const [dataList, setDataList] = useState<ExportDataResponse[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(ITEMS_PER_PAGE_OPTIONS[5]);

  const { username } = useAppSelector(getUserState);

  // Track component mount state to prevent state updates after unmount
  const isMountedRef = useRef<boolean>(true);
  const pollingAttemptsRef = useRef<number>(0);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Custom hook for local state pagination
  const { pageItems, setCurrentPage, currentPage, totalPages } = usePagination(dataList, 3);

  const pollExportResult = useCallback(
    async (taskId: string, currentPage: number) => {
      try {
        const result = await fetchGetExportResult({
          predictorId: valueId,
          taskId,
          page: currentPage,
          pageSize: itemsPerPage,
        });

        if (result.task_status === "pending") {
          // Check if max polling attempts reached
          if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
            setIsFetching(false);
            enqueueSnackbar("Export is taking too long. Please try again later.", { variant: "warning" });
            return;
          }

          pollingAttemptsRef.current += 1;

          // Keep polling if component is still mounted
          setTimeout(() => {
            if (isMountedRef.current) {
              pollExportResult(taskId, currentPage);
            }
          }, 3000);
          return;
        }

        if (result.task_status === "failure") {
          setIsFetching(false);
          enqueueSnackbar("We're sorry, but an error has occurred", { variant: "error" });
          return;
        }

        // Task completed - check for next page
        if (typeof result.next === "number") {
          setIsLoadingAll(true);
          // Create next export task
          const nextTask = await fetchCreateExportTask({
            predictorId: valueId,
            page: result.next,
            pageSize: itemsPerPage,
          });
          if (nextTask.task_id) {
            pollingAttemptsRef.current = 0;
            setTimeout(() => {
              if (isMountedRef.current) {
                pollExportResult(nextTask.task_id, result.next);
              }
            }, 3000);
          }
        } else {
          setIsFetching(false);
          setIsLoadingAll(false);
        }

        // Prevent duplicate data
        setDataList((prev) => {
          const findDuplicate = prev.find((i) => i.key === result.key);
          if (findDuplicate) return prev;
          return [...prev, { ...result, page: currentPage }];
        });
      } catch (error) {
        console.error(error);
        setIsFetching(false);
        enqueueSnackbar("Failed to get export result", { variant: "error" });
      }
    },
    [valueId, itemsPerPage, enqueueSnackbar]
  );

  const onExport = useCallback(async () => {
    setDataList([]);
    setIsFetching(true);
    setIsLoadingAll(true);
    pollingAttemptsRef.current = 0;

    try {
      const result = await fetchCreateExportTask({
        predictorId: valueId,
        page: 1,
        pageSize: itemsPerPage,
      });

      if (result.task_id && result.task_status === "pending") {
        setTimeout(() => {
          if (isMountedRef.current) {
            pollExportResult(result.task_id, 1);
          }
        }, 1000);
      }
    } catch (error) {
      console.error(error);
      setIsFetching(false);
      setIsLoadingAll(false);
      enqueueSnackbar("Failed to create export task", { variant: "error" });
    }
  }, [valueId, itemsPerPage, pollExportResult, enqueueSnackbar]);

  return (
    <Box component="form">
      <Grid
        container
        spacing={2}
        direction="column"
        sx={{
          justifyContent: "center",
          alignItems: "center",
          alignContent: "center",
        }}
      >
        <Grid item sx={{ marginTop: "5%", width: "75%" }}>
          <Typography variant="subtitle" textTransform="uppercase" className="text-gray-600">
            ADMIN {username}
          </Typography>
          <Typography variant="subtitle" textTransform="uppercase">
            {`Export ${QUERY_NAMING.uiNamePlural}`}
          </Typography>

          <Divider sx={{ borderWidth: "1px", margin: "1rem 0" }} />

          <Box className="mb-4">
            <Box className="mb-2">
              <Typography variant="subtitle" fontWeight="bold" textTransform="uppercase">
                Detector ID
              </Typography>
            </Box>
            <TextField
              fullWidth
              id="simple-textfield-value"
              placeholder="Enter the Detector ID"
              value={valueId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValueId(e.target.value)}
              size="small"
            />
          </Box>

          <Box className="flex justify-center gap-2">
            <InputLabel id="image-queries-per-page-label" className="self-center">
              IQ per file
            </InputLabel>
            <Select
              id="image-queries-per-page-select"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              sx={{ padding: 0 }}
              className="normal-case mt-auto"
              classes={{ select: "text-base" }}
              disabled={isFetching || !valueId}
            >
              {ITEMS_PER_PAGE_OPTIONS.map((limitMapping) => (
                <MenuItem
                  key={limitMapping}
                  value={limitMapping}
                  classes={{
                    root: "text-base",
                    selected: "text-base",
                  }}
                >
                  {limitMapping}
                </MenuItem>
              ))}
            </Select>
            <Button
              type="submit"
              endIcon={isFetching && <CircularProgress size={20} sx={{ color: "white" }} />}
              disabled={isFetching || !valueId}
              className="px-8 py-1"
              variant="contained"
              size="large"
              onClick={onExport}
            >
              <Typography variant="subtitle" className="uppercase">
                {isFetching ? "Exporting" : "Export"}
              </Typography>
            </Button>
          </Box>

          {isLoadingAll && (
            <Box className="mt-10 flex justify-center flex-col items-center gap-8">
              <Box className="flex justify-between items-center gap-8">
                {pageItems &&
                  pageItems?.map((item) => (
                    <Box className="flex flex-col items-center" key={item.page}>
                      <Box>
                        <Link href={item.download_url}>
                          <Typography fontWeight="bold" textTransform="uppercase">
                            Download (Page {item.page})
                          </Typography>
                        </Link>
                      </Box>
                      <Box>
                        <Typography variant="detail">
                          ZIP File Size: {Math.round((item.download_size_bytes * 100) / 1000000) / 100} MB
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>

              {pageItems.length > 0 && (
                <Box className="w-48 flex justify-between items-center gap-2">
                  <IconButton disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <ChevronLeftIcon />
                  </IconButton>

                  <Typography variant="detail">
                    Page {currentPage} - {totalPages}
                  </Typography>

                  <IconButton disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
